/**
 * The editorial look: Playfair display headings, letterspaced caps for small
 * type, fields as rules rather than boxes, near-black in place of the club
 * accent.
 *
 * It is opt-in per club (`branding.theme === 'editorial'`), so a tenant that
 * has not asked for it keeps the platform's default styling. Pages branch on
 * `isEditorialTheme(tenant)` and pick the matching class strings from here,
 * which is what keeps the language consistent as more pages adopt it — there
 * is no shared component layer in this codebase to hang it off instead.
 */

export const SERIF: React.CSSProperties = { fontFamily: "'Playfair Display',serif" };

/**
 * The club's sans, used for the site furniture — header wordmark and nav,
 * footer. Gothic A1 is the University of Chicago's brand sans-serif.
 *
 * Both faces are declared here rather than at each call site so correcting
 * the family is a one-line change. Loaded in public/index.html.
 */
export const BRAND_SANS: React.CSSProperties = {
  fontFamily: "'Gothic A1',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
};

/**
 * Figures that have to line up in a column — a running clock, a rating, a
 * move table. Tailwind's `font-mono` falls through to Courier here, whose
 * digits are too light and too wide to read at a glance under time pressure;
 * JetBrains Mono is already loaded for the site and has even, tabular figures.
 */
export const MONO: React.CSSProperties = {
  fontFamily: "'JetBrains Mono',ui-monospace,SFMono-Regular,Menlo,monospace",
  fontVariantNumeric: 'tabular-nums',
};

export function isEditorialTheme(tenant: { branding?: any } | null | undefined): boolean {
  return String(tenant?.branding?.theme || '').toLowerCase() === 'editorial';
}

/** Small uppercase label sitting above a field or section. */
export const eyebrow =
  'text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400';

/** The club name / section kicker, a step wider than `eyebrow`. */
export const kicker =
  'text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400';

/** Rule-under-the-text field: no box, so the type carries the form. */
export const input =
  'w-full bg-transparent border-0 border-b border-gray-300 rounded-none px-0 py-2.5 text-[15px] text-gray-900 placeholder:text-gray-300 transition-colors focus:outline-none focus:ring-0 focus:border-gray-900 disabled:text-gray-400 disabled:border-gray-200';

export const label = `block ${eyebrow} mb-1.5`;

export const primaryButton =
  'w-full bg-gray-900 text-white py-3.5 text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors hover:bg-black disabled:opacity-40';

/** Square outline button, used in rows of equal-weight secondary actions. */
export const secondaryButton =
  'py-2.5 px-4 border border-gray-200 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-600 transition-colors hover:border-gray-900 hover:text-gray-900 disabled:opacity-40';

export const textLink =
  'text-gray-900 underline underline-offset-4 decoration-gray-300 hover:decoration-gray-900 transition-colors';

/**
 * Display headings. Pair with the `SERIF` style object.
 *
 * Playfair has tall ascenders and a high x-height, so the tight leading a
 * grotesk wants leaves it cramped — these sit nearer 1.1 and come down a step
 * in size, with a little negative tracking to close the gaps that open up at
 * display sizes.
 */
export const h1 =
  'text-[2rem] lg:text-[2.375rem] leading-[1.12] tracking-[-0.015em] text-gray-900';
export const h2 =
  'text-[1.375rem] lg:text-[1.5rem] leading-[1.25] tracking-[-0.01em] text-gray-900';

/** Hairline-bordered panel — the editorial answer to a rounded card. */
export const panel = 'border border-gray-200 bg-white';

export const mutedText = 'text-sm text-gray-500 leading-relaxed';
